import React from 'react';
import { Button, Col } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';

import CaptionButton from '../../../components/CaptionButton';
import ElementWithPermissions from '../../../components/ElementWithPermissions';
import confirm from '../../../components/ConfirmDialogModal';
import { CaptionButtonContainer, ButtonsContainer } from './styled';

import messages from './messages';
import { useQueryClient } from 'react-query';

type Props = {
  applicationId: string,
  doNotRenew: boolean,
  lease: Object,
  removeLease: Function,
  residentId: string,
  selectPreviousLease: Function,
  handleTransferComplete: Function,
  intl: Object,
  underEviction: boolean,
  canComplete: boolean,
  formDirty: boolean,
  householdId: string,
  hideCancelTransfer: boolean,
  hideCompleteTransfer: boolean,
};

export const TransferButtons = ({
  applicationId,
  doNotRenew,
  lease,
  removeLease,
  residentId,
  selectPreviousLease,
  handleTransferComplete,
  intl,
  underEviction,
  canComplete,
  hideCancelTransfer,
  hideCompleteTransfer,
}: Props) => {
  const queryClient = useQueryClient();

  const refreshData = () => {
    // These are two DISTINCT react-query cache entries, so they must be invalidated as separate
    // prefix matches. The compound key covers the transfer/NTV validation query
    // (['household','lease','validateTransferLease', ...]); 'startTransferEligibility' is its own
    // top-level key (['startTransferEligibility', org, prop, household]) driving the Snapshot Start
    // Transfer button and the Lease Data Tab Scheduled Move-In lock, so it needs its own call.
    queryClient.invalidateQueries([
      'household',
      'lease',
      'validateTransferLease',
    ]);
    queryClient.invalidateQueries(['startTransferEligibility']);
    // Cancelling undoes the transfer's certifications, so refresh the certs/qualification caches
    // too (mirrors the invalidations done when the transfer is created).
    queryClient.invalidateQueries([
      'getHouseholdProfileAffordableQualificationsByProgram',
    ]);
    queryClient.invalidateQueries(['getQualificationHistory']);
  };

  const renderConfirmDialog = () => {
    confirm(intl.formatMessage(messages.cancelTransferConfirmationHeader), {
      body: intl.formatMessage(messages.cancelTransferConfirmationBody),
    }).then(() => {
      selectPreviousLease();
      removeLease(applicationId, lease.id, residentId, refreshData);
    });
  };

  const disableCompleteTransfer = underEviction || doNotRenew || !canComplete;

  const getCompleteRenewalHelpBlockMessage = () => {
    if (underEviction) return messages.completeDisabledUnderEviction;
    if (doNotRenew) return messages.completeDisabledDoNotRenew;
    if (!canComplete) return messages.completeDisabled;
    return messages.completeDisabled;
  };

  return (
    <Col md={6}>
      <ButtonsContainer className="pull-right padtop20 padbottom10">
        <CaptionButtonContainer>
          {/* When the Unit Transfers flag is on, completing happens on the Resident Transfer card
              (ENG-30151); hide this legacy Complete Transfer button to avoid two competing paths. */}
          {!hideCompleteTransfer && (
            <ElementWithPermissions scope={['resident-move-out']}>
              <CaptionButton
                className="btn btn-tertiary up10"
                type="button"
                onClick={handleTransferComplete}
                disabled={disableCompleteTransfer}
                caption={
                  disableCompleteTransfer && (
                    <FormattedMessage
                      {...getCompleteRenewalHelpBlockMessage()}
                    />
                  )
                }
              >
                <FormattedMessage {...messages.completeTransferButton} />
              </CaptionButton>
            </ElementWithPermissions>
          )}
        </CaptionButtonContainer>
        <CaptionButtonContainer>
          {/* When the Unit Transfers flag is on, canceling happens on the Resident Transfer card
              (ENG-30150); hide this legacy Cancel Transfer button to avoid two competing paths. */}
          {!hideCancelTransfer && (
            <ElementWithPermissions scope={['lease-transfer-start']}>
              <Button
                className="btn btn-callto up10"
                onClick={renderConfirmDialog}
              >
                <FormattedMessage {...messages.cancelTransferButton} />
              </Button>
            </ElementWithPermissions>
          )}
        </CaptionButtonContainer>
      </ButtonsContainer>
    </Col>
  );
};

export default injectIntl(TransferButtons);
